import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../utils/supabaseClient'
import { Movie, MovieGroup } from '../types'
import {
  Box, Grid, Card, CardContent, CardMedia, Typography, Chip, Tabs, Tab, TextField, InputAdornment, Skeleton,
} from '@mui/material'
import { Search, Movie as MovieIcon, Tv } from '@mui/icons-material'

const MovieList: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [movies, setMovies] = useState<Movie[]>([])
  const [groups, setGroups] = useState<MovieGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    fetchMovies()
  }, [])

  const fetchMovies = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('movies')
      .select('*')
      .order('title')
      .order('episode_number')

    if (!error && data) {
      setMovies(data)
      const groupMap = new Map<string, MovieGroup>()
      for (const m of data) {
        const key = m.title
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            title: m.title,
            category: m.category,
            total_episodes: m.total_episodes,
            episodes: [],
          })
        }
        groupMap.get(key)!.episodes.push(m)
      }
      setGroups(Array.from(groupMap.values()))
    }
    setLoading(false)
  }

  const filtered = groups.filter(g => {
    const matchSearch = !search || g.title.toLowerCase().includes(search.toLowerCase())
    const matchTab = tab === 0 || (tab === 1 && g.category === 'movie') || (tab === 2 && g.category === 'series')
    return matchSearch && matchTab
  })

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="搜索电影/剧集..."
          size="small" variant="outlined"
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: '#1a1a2e' } }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><Search sx={{ color: '#aaa' }} /></InputAdornment>,
            },
          }}
        />
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { color: '#aaa' }, '& .Mui-selected': { color: '#e50914' } }}>
          <Tab label="全部" />
          <Tab icon={<MovieIcon />} iconPosition="start" label="电影" />
          <Tab icon={<Tv />} iconPosition="start" label="剧集" />
        </Tabs>
      </Box>

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid key={i} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
              <Skeleton variant="rounded" height={200} sx={{ bgcolor: '#1a1a2e' }} />
              <Skeleton width="60%" sx={{ mt: 1, bgcolor: '#1a1a2e' }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(group => (
            <Grid key={group.title} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
              <Card
                sx={{ bgcolor: '#1a1a2e', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                onClick={() => navigate(`/movie/${encodeURIComponent(group.title)}`)}
              >
                <CardMedia
                  component="div"
                  sx={{ height: 200, bgcolor: '#2a2a4a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {group.episodes[0]?.cover_url ? (
                    <CardMedia component="img" image={group.episodes[0].cover_url} sx={{ height: 200 }} />
                  ) : (
                    <MovieIcon sx={{ fontSize: 60, color: '#555' }} />
                  )}
                </CardMedia>
                <CardContent sx={{ p: 1.5 }}>
                  <Typography variant="body2" noWrap sx={{ color: '#fff', fontWeight: 'bold' }}>
                    {group.title}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={group.category === 'series' ? `共${group.episodes.length}集` : '电影'}
                      sx={{ bgcolor: '#e50914', color: '#fff', height: 20, fontSize: 11 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, color: '#666' }}>
          <Typography>没有找到相关内容</Typography>
        </Box>
      )}
    </Box>
  )
}

export default MovieList
